'use client';

import PropTypes from 'prop-types';
import {getThumbnailUrl} from "@/utils/image-utils.ts";

import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";

import {paths} from 'src/routes/paths';
import {RouterLink} from "src/routes/components";

import {fDate} from "src/utils/format-time";

import {useAuthContext} from "src/auth/hooks";

import Iconify from 'src/components/iconify';

function RecipeHead({recipeData}) {
  const {user} = useAuthContext();

  const authorPhotoUrl = getThumbnailUrl(recipeData.authorPhoto, '270_270');

  return (
    <Grid container spacing={2}>
      <Grid xs={12} sm={7} md={8} lg={9} item>
        {recipeData.isUserPreview && (
          <Alert severity="warning">Przepis oczekuje na weryfikację przez moderatora. Tymczasem widzisz go tylko Ty.</Alert>
        )}
        <Stack direction="row" sx={{mb: 3}}>
          <Typography variant="h4">
            {recipeData.name}
          </Typography>
          {parseInt(recipeData.authorId, 10) === user?.pimcoreUserId && (
            <Link component={RouterLink} href={paths.recipe.edit(recipeData.recipeAccessKey ? `rac_${recipeData.recipeAccessKey}` : recipeData.pimcoreRecipeId)} variant="body2" sx={{color: 'text.secondary', display: 'flex', alignItems: 'center', ml: 1}}>
              <Iconify icon="bx:edit" width="18px" sx={{color: 'text.secondary'}}/>
              Edytuj przepis
            </Link>
          )}
        </Stack>

        <Stack spacing={3} direction="row" flexWrap="wrap" alignItems="center">
          {recipeData.ratingCount > 0 && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{typography: 'body2'}}>
              <Iconify icon="eva:star-fill" sx={{color: 'warning.main'}}/>
              <Box component="span" sx={{typography: 'subtitle2'}}>
                {(Math.round(recipeData.ratingValue * 10) / 10).toFixed(1)}
              </Box>
              <Link sx={{color: 'text.secondary'}}>({recipeData.ratingCount})</Link>
            </Stack>
          )}

          <Stack direction="row" alignItems="center" spacing={0.5} sx={{typography: 'body2'}}>
            <Iconify icon="solar:calendar-date-bold" sx={{color: 'error.main'}}/>
            {`Dodano: ${fDate(recipeData.createdAt * 1000)}`}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.5} sx={{typography: 'body2'}}>
            <Iconify icon="carbon:view-filled" sx={{color: 'success.main'}}/>
            {`Wyświetleń: ${recipeData.displayCount ?? 1}`}
          </Stack>
        </Stack>
      </Grid>
      <Grid xs={12} sm={5} md={4} lg={3} item sx={{
        textAlign: {sm: 'right'}
      }}>
        {recipeData.authorName && <Stack
          component={Paper}
          variant="outlined"
          spacing={2}
          direction="row"
          sx={{ p:1, mt: {xs: 2, sm: 0}, display: {sm: 'inline-flex'}, textAlign: 'left' }}
        >
          <Link
            component={RouterLink}
            href={paths.user.profile(recipeData.authorId)}
            sx={{
              display: 'block',
              borderRadius: 1,
              '&:hover': {
                opacity: 0.8
              }
            }}
          >
            <Avatar
              alt={recipeData.authorName}
              src={authorPhotoUrl}
              variant="rounded"
              sx={{ width: 50, height: 50 }}
            />
          </Link>

          <Stack sx={{justifyContent: 'center'}}>
            <Typography variant="body2">Autor przepisu</Typography>
            <Link
              component={RouterLink}
              href={paths.user.profile(recipeData.authorId)}
              variant="subtitle1"
              sx={{
                color: 'text.primary',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'primary.main'
                }
              }}
            >
              {recipeData.authorName}
            </Link>
          </Stack>
        </Stack>}
      </Grid>
    </Grid>
  );
}

RecipeHead.propTypes = {
  recipeData: PropTypes.object
};

export default RecipeHead;
